import React, { useMemo, useState } from 'react';
import { LayoutChangeEvent, View } from 'react-native';
import { useTranslation } from 'react-i18next';
import Svg, {
  Circle,
  Defs,
  G,
  Line,
  LinearGradient,
  Path,
  Stop,
  Text as SvgText,
} from 'react-native-svg';

import { TeamCrest } from '@/components/matches/TeamCrest';
import { AppText } from '@/components/ui/AppText';
import { Card } from '@/components/ui/Card';
import { useThemePalette } from '@/config/theme';
import type { MatchShot, MatchXgStats, TeamRef } from '@/lib/data/provider';
import { localizeDigits } from '@/lib/i18n/format';
import { useLocale } from '@/lib/i18n/LocaleProvider';
import { localizeTeamName } from '@/lib/i18n/footballNames';

const CHART_HEIGHT = 168;
const PAD_TOP = 22; // headroom for the goal glyphs that sit at the top edge
const PAD_BOTTOM = 18;
const PAD_X = 8;
/** Minimum right edge of the minute axis — full time is at least 90'. */
const MIN_AXIS_MINUTE = 90;

function twoDecimals(value: number): string {
  return (Math.round(value * 100) / 100).toFixed(2);
}

interface CumPoint {
  minute: number;
  cum: number;
}

interface GoalMark {
  minute: number;
  cum: number;
}

/**
 * Build a smooth, densely-sampled cumulative-xG series for one side. We keep the
 * true step "events" (each shot bumps the running total) but then resample the
 * curve at every minute so the rendered path is made of many fine segments —
 * this reads as a flowing curve, not a staircase. Each goal's minute + the xG
 * total *at* that goal is captured separately so we can drop a ball glyph on it.
 */
function buildSeries(
  shots: MatchShot[],
  side: 'home' | 'away',
  axisMinute: number,
): { points: CumPoint[]; goals: GoalMark[]; final: number } {
  const ordered = shots
    .filter((shot) => shot.team_side === side)
    .slice()
    .sort((a, b) => a.minute - b.minute);

  // Running cumulative total keyed by minute (clamped into the axis range).
  const goals: GoalMark[] = [];
  let cum = 0;
  // cumAt(m) = total xG accumulated up to and including minute m.
  const bumps: { minute: number; cum: number }[] = [];
  for (const shot of ordered) {
    cum += Math.max(0, shot.xg);
    const minute = Math.min(Math.max(0, shot.minute), axisMinute);
    bumps.push({ minute, cum });
    if (shot.is_goal) goals.push({ minute, cum });
  }
  const final = cum;

  // Resample at every minute 0→axis. Between two shot events the cumulative
  // curve eases up smoothly (cosine ramp) instead of jumping, which is what
  // makes the line look like FotMob's flowing momentum rather than a step race.
  const points: CumPoint[] = [];
  let idx = 0;
  let prevMin = 0;
  let prevCum = 0;
  for (let m = 0; m <= axisMinute; m += 1) {
    // Advance through any bumps whose minute we've now passed.
    while (idx < bumps.length && bumps[idx].minute <= m) {
      prevMin = bumps[idx].minute;
      prevCum = bumps[idx].cum;
      idx += 1;
    }
    if (idx < bumps.length) {
      const next = bumps[idx];
      const span = next.minute - prevMin;
      if (span > 0 && m > prevMin) {
        const t = (m - prevMin) / span;
        // Smoothstep ease so the rise into each shot is gentle, not linear.
        const eased = t * t * (3 - 2 * t);
        points.push({ minute: m, cum: prevCum + (next.cum - prevCum) * eased });
        continue;
      }
    }
    points.push({ minute: m, cum: prevCum });
  }
  return { points, goals, final };
}

/** Bzzoiro's ready per-minute cumulative xG row (already cumulative by minute). */
type XgPerMinutePoint = NonNullable<MatchXgStats['xg_per_minute']>[number];

/**
 * Build the SAME `{ points, goals, final }` shape from Bzzoiro's authoritative
 * per-minute cumulative series. We read `cum_home`/`cum_away` straight off each
 * minute (no shot re-derivation, so it matches the provider exactly), resampling
 * to one point per minute across the axis. Goal marks still come from the shots
 * (the per-minute feed carries no goal flag); each goal is dropped onto the curve
 * at the cumulative value for its minute so the ball glyph sits on the line.
 */
function buildSeriesFromCum(
  series: XgPerMinutePoint[],
  side: 'home' | 'away',
  shots: MatchShot[],
  axisMinute: number,
): { points: CumPoint[]; goals: GoalMark[]; final: number } {
  // Cumulative-by-minute lookup, clamped non-decreasing and into the axis range.
  const byMinute = new Map<number, number>();
  let running = 0;
  for (const row of series.slice().sort((a, b) => a.m - b.m)) {
    const minute = Math.min(Math.max(0, Math.round(row.m)), axisMinute);
    const cum = side === 'home' ? row.cum_home : row.cum_away;
    running = Math.max(running, Math.max(0, cum));
    byMinute.set(minute, running);
  }

  // Resample every minute 0→axis, carrying the last known cumulative forward so
  // gaps stay flat (the curve only rises when the provider says it does).
  const points: CumPoint[] = [];
  let carried = 0;
  const cumAt = (m: number): number => {
    if (byMinute.has(m)) carried = byMinute.get(m) as number;
    return carried;
  };
  for (let m = 0; m <= axisMinute; m += 1) {
    points.push({ minute: m, cum: cumAt(m) });
  }
  const final = points.length > 0 ? points[points.length - 1].cum : 0;

  // Goal marks from the shots of this side, placed on the cumulative curve.
  const goals: GoalMark[] = shots
    .filter((shot) => shot.team_side === side && shot.is_goal)
    .map((shot) => {
      const minute = Math.min(Math.max(0, Math.round(shot.minute)), axisMinute);
      return { minute, cum: points[minute]?.cum ?? final };
    })
    .sort((a, b) => a.minute - b.minute);

  return { points, goals, final };
}

/**
 * "xG momentum" — two SMOOTH cumulative-xG curves (home accent, away live)
 * climbing 0→FT across the match minutes, each filled as a soft translucent area
 * down to the baseline, with a ball glyph dropped at the top for every goal and
 * the final cumulative xG labelled at the end of each line. One lightweight
 * react-native-svg node. The caller hides the block when there are <2 shots.
 */
export function XgMomentum({
  shots,
  stats,
  livePerMinute = null,
  homeTeam,
  awayTeam,
}: {
  shots: MatchShot[];
  /** When present, `stats.xg_per_minute` drives the curves authoritatively. */
  stats?: MatchXgStats | null;
  /**
   * The LIVE per-minute cumulative xG series off the live snapshot (refreshed
   * ~every 3 min by sync-bzzoiro-live). When present it takes precedence over the
   * static `stats.xg_per_minute`, so the race climbs mid-match; null on
   * finished/upcoming matches (or early live minutes) → fall back to `stats`.
   */
  livePerMinute?: MatchXgStats['xg_per_minute'] | null;
  homeTeam: TeamRef | null;
  awayTeam: TeamRef | null;
}) {
  const palette = useThemePalette();
  const { t } = useTranslation();
  const { locale, arabicNumerals } = useLocale();
  const [width, setWidth] = useState(0);

  const onLayout = (e: LayoutChangeEvent) => setWidth(e.nativeEvent.layout.width);

  const { home, away, maxCum, axisMinute } = useMemo(() => {
    // Bzzoiro's per-minute cumulative xG, when present, is authoritative. The
    // LIVE series (when supplied) wins over the static stats one so the race
    // climbs while the match is in play.
    const perMinute = livePerMinute ?? stats?.xg_per_minute ?? null;
    const hasPerMinute = Array.isArray(perMinute) && perMinute.length > 0;

    // Right edge = the latest minute we have data for, but never before 90' (FT).
    const lastShotMinute = shots.reduce((mx, s) => Math.max(mx, s.minute), 0);
    const lastSeriesMinute = hasPerMinute
      ? (perMinute as XgPerMinutePoint[]).reduce((mx, p) => Math.max(mx, p.m), 0)
      : 0;
    const axis = Math.max(MIN_AXIS_MINUTE, Math.ceil(Math.max(lastShotMinute, lastSeriesMinute)));

    const homeS = hasPerMinute
      ? buildSeriesFromCum(perMinute as XgPerMinutePoint[], 'home', shots, axis)
      : buildSeries(shots, 'home', axis);
    const awayS = hasPerMinute
      ? buildSeriesFromCum(perMinute as XgPerMinutePoint[], 'away', shots, axis)
      : buildSeries(shots, 'away', axis);
    const peak = Math.max(homeS.final, awayS.final, 0.5);
    return { home: homeS, away: awayS, maxCum: peak, axisMinute: axis };
  }, [shots, stats, livePerMinute]);

  const innerW = Math.max(0, width - PAD_X * 2);
  const innerH = CHART_HEIGHT - PAD_TOP - PAD_BOTTOM;
  const baselineY = PAD_TOP + innerH;
  const xFor = (minute: number) =>
    PAD_X + (Math.min(minute, axisMinute) / axisMinute) * innerW;
  const yFor = (cum: number) => PAD_TOP + innerH - (cum / maxCum) * innerH;

  /**
   * A smooth cubic-Bézier path through the resampled points (Catmull-Rom → Bézier
   * conversion). Many fine points + curved control handles = a coherent, glassy
   * curve with no visible stair-steps.
   */
  const smoothLine = (points: CumPoint[]): string => {
    if (points.length === 0 || innerW <= 0) return '';
    const pts = points.map((p) => ({ x: xFor(p.minute), y: yFor(p.cum) }));
    if (pts.length === 1) return `M ${pts[0].x} ${pts[0].y}`;
    let d = `M ${pts[0].x} ${pts[0].y}`;
    for (let i = 0; i < pts.length - 1; i += 1) {
      const p0 = pts[i - 1] ?? pts[i];
      const p1 = pts[i];
      const p2 = pts[i + 1];
      const p3 = pts[i + 2] ?? p2;
      const c1x = p1.x + (p2.x - p0.x) / 6;
      const c1y = p1.y + (p2.y - p0.y) / 6;
      const c2x = p2.x - (p3.x - p1.x) / 6;
      const c2y = p2.y - (p3.y - p1.y) / 6;
      d += ` C ${c1x} ${c1y} ${c2x} ${c2y} ${p2.x} ${p2.y}`;
    }
    return d;
  };

  /** The same smooth curve, then closed down to the baseline → fillable area. */
  const areaPath = (points: CumPoint[]): string => {
    const line = smoothLine(points);
    if (!line) return '';
    const lastX = xFor(points[points.length - 1].minute);
    const firstX = xFor(points[0].minute);
    return `${line} L ${lastX} ${baselineY} L ${firstX} ${baselineY} Z`;
  };

  /** A football glyph: white ball + ring + a couple of dark panel marks. */
  const Ball = ({ cx, cy, tint }: { cx: number; cy: number; tint: string }) => (
    <G>
      <Circle cx={cx} cy={cy} r={7.5} fill={tint} opacity={0.28} />
      <Circle cx={cx} cy={cy} r={5} fill={palette.text} stroke={tint} strokeWidth={1.5} />
      <Circle cx={cx} cy={cy} r={1.6} fill={palette.deep} />
      <Circle cx={cx - 2.4} cy={cy + 1.8} r={1} fill={palette.deep} opacity={0.7} />
      <Circle cx={cx + 2.4} cy={cy + 1.8} r={1} fill={palette.deep} opacity={0.7} />
    </G>
  );

  const homeFinal = home.final;
  const awayFinal = away.final;

  return (
    <Card className="overflow-hidden">
      <View className="flex-row items-center justify-between px-4 pb-1 pt-3">
        <AppText variant="micro" weight="bold" className="uppercase tracking-widest text-muted">
          {t('match.momentum')}
        </AppText>
      </View>

      {/* Final cumulative xG per team, flanking the chart. */}
      <View className="flex-row items-center justify-between px-4 pb-1 pt-1">
        <View className="flex-1 flex-row items-center gap-1.5">
          <TeamCrest uri={homeTeam?.logo_url} name={localizeTeamName(homeTeam, locale)} size="xs" />
          <AppText variant="caption" weight="bold" tabular className="text-volt-bright">
            {localizeDigits(twoDecimals(homeFinal), arabicNumerals)}
          </AppText>
        </View>
        <View className="flex-1 flex-row items-center justify-end gap-1.5">
          <AppText variant="caption" weight="bold" tabular className="text-soft">
            {localizeDigits(twoDecimals(awayFinal), arabicNumerals)}
          </AppText>
          <TeamCrest uri={awayTeam?.logo_url} name={localizeTeamName(awayTeam, locale)} size="xs" />
        </View>
      </View>

      {/* The chart is always read left→right (minute axis), even in RTL. */}
      <View
        className="px-4 pb-0.5"
        style={{ height: CHART_HEIGHT, direction: 'ltr' }}
        onLayout={onLayout}
      >
        {width > 0 ? (
          <Svg width={innerW + PAD_X * 2} height={CHART_HEIGHT}>
            <Defs>
              <LinearGradient id="homeArea" x1="0" y1="0" x2="0" y2="1">
                <Stop offset="0" stopColor={palette.volt} stopOpacity={0.26} />
                <Stop offset="1" stopColor={palette.volt} stopOpacity={0.02} />
              </LinearGradient>
              <LinearGradient id="awayArea" x1="0" y1="0" x2="0" y2="1">
                <Stop offset="0" stopColor={palette.teamAway} stopOpacity={0.16} />
                <Stop offset="1" stopColor={palette.teamAway} stopOpacity={0.02} />
              </LinearGradient>
            </Defs>

            {/* Baseline. */}
            <Line
              x1={PAD_X}
              y1={baselineY}
              x2={innerW + PAD_X}
              y2={baselineY}
              stroke={palette.pitchLine}
              strokeWidth={1}
              opacity={0.22}
            />

            {/* Half-time dashed guide + "HT" label. */}
            <Line
              x1={xFor(45)}
              y1={PAD_TOP - 6}
              x2={xFor(45)}
              y2={baselineY}
              stroke={palette.pitchLine}
              strokeWidth={1}
              strokeDasharray="3 5"
              opacity={0.28}
            />
            <SvgText
              x={xFor(45)}
              y={PAD_TOP - 10}
              fill={palette.muted}
              fontSize={8.5}
              fontWeight="700"
              textAnchor="middle"
            >
              {t('match.halfTime')}
            </SvgText>

            {/* Areas first (so the lines sit crisply on top). */}
            <Path d={areaPath(away.points)} fill="url(#awayArea)" stroke="none" />
            <Path d={areaPath(home.points)} fill="url(#homeArea)" stroke="none" />

            {/* Away line — same cyan family, dimmed so the two curves separate. */}
            <Path
              d={smoothLine(away.points)}
              stroke={palette.teamAway}
              strokeWidth={2}
              fill="none"
              opacity={0.55}
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            {/* Home line — the bright lead accent. */}
            <Path
              d={smoothLine(home.points)}
              stroke={palette.volt}
              strokeWidth={2.75}
              fill="none"
              strokeLinecap="round"
              strokeLinejoin="round"
            />

            {/* End-of-line value tags. */}
            {homeFinal > 0 ? (
              <Circle cx={xFor(axisMinute)} cy={yFor(homeFinal)} r={3.2} fill={palette.volt} />
            ) : null}
            {awayFinal > 0 ? (
              <Circle
                cx={xFor(axisMinute)}
                cy={yFor(awayFinal)}
                r={2.6}
                fill={palette.teamAway}
                opacity={0.7}
              />
            ) : null}

            {/* Goal glyphs at the top, tinted by the scoring team. */}
            {away.goals.map((g, i) => (
              <G key={`ag-${i}`}>
                <Line
                  x1={xFor(g.minute)}
                  y1={PAD_TOP - 2}
                  x2={xFor(g.minute)}
                  y2={yFor(g.cum)}
                  stroke={palette.teamAway}
                  strokeWidth={1}
                  strokeDasharray="2 3"
                  opacity={0.4}
                />
                <Ball cx={xFor(g.minute)} cy={PAD_TOP - 5} tint={palette.teamAway} />
              </G>
            ))}
            {home.goals.map((g, i) => (
              <G key={`hg-${i}`}>
                <Line
                  x1={xFor(g.minute)}
                  y1={PAD_TOP - 2}
                  x2={xFor(g.minute)}
                  y2={yFor(g.cum)}
                  stroke={palette.volt}
                  strokeWidth={1}
                  strokeDasharray="2 3"
                  opacity={0.5}
                />
                <Ball cx={xFor(g.minute)} cy={PAD_TOP - 5} tint={palette.volt} />
              </G>
            ))}
          </Svg>
        ) : null}
      </View>

      {/* Minute axis: 0' (kickoff) … FT (full time / last minute). */}
      <View className="flex-row items-center justify-between px-4 pb-3" style={{ direction: 'ltr' }}>
        <AppText variant="micro" weight="medium" tabular className="text-muted">
          {localizeDigits('0', arabicNumerals)}&apos;
        </AppText>
        <AppText variant="micro" weight="bold" tabular className="text-muted">
          {t('match.fullTime')}
        </AppText>
      </View>

      <View className="border-t border-line px-4 py-2">
        <AppText variant="micro" className="text-muted">
          {t('match.momentumHint')}
        </AppText>
      </View>
    </Card>
  );
}
